import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import DiscordFomoLock from "@/components/DiscordFomoLock";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Zap, Sparkles, CheckCircle2, AlertTriangle, Lock, ArrowRight, Loader2,
} from "lucide-react";
import api from "@/lib/api";

/**
 * Trade Health Score — a lead-magnet-turned-retention-tool.
 *
 * Anonymous visitors are bounced to /login?redirect=/health-check so we
 * capture the account before running any AI. Free-tier accounts have a
 * hard 2-use lifetime cap (enforced server-side); Basic/Premium is
 * unlimited. When the cap is hit we swap the form for an upgrade card.
 */
export default function TradeHealth() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [quota, setQuota] = useState(null);
  const [form, setForm] = useState({
    instrument: "",
    direction: "long",
    entry: "",
    stop: "",
    target: "",
    size: "",
    account_size: "",
    rationale: "",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // Redirect anonymous visitors — we intentionally require signup.
  useEffect(() => {
    if (user === false) {
      navigate("/login?redirect=/health-check", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!user || typeof user !== "object") return;
    api.get("/trade-health/quota")
      .then((r) => setQuota(r.data))
      .catch(() => setQuota({ unlimited: false, remaining: 0, used: 0, limit: 2 }));
  }, [user]);

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    // Client-side validation — keep it light, backend is source of truth.
    for (const k of ["instrument", "entry", "stop", "target"]) {
      if (!form[k] || (k !== "instrument" && Number.isNaN(parseFloat(form[k])))) {
        toast.error(t(`tradeHealthUi.pleaseEnter`, { field: t(`tradeHealthUi.field_${k}`) }));
        return;
      }
    }
    setBusy(true);
    setResult(null);
    try {
      const payload = {
        instrument: form.instrument.trim().toUpperCase(),
        direction: form.direction,
        entry: parseFloat(form.entry),
        stop: parseFloat(form.stop),
        target: parseFloat(form.target),
        size: form.size ? parseFloat(form.size) : undefined,
        account_size: form.account_size ? parseFloat(form.account_size) : undefined,
        rationale: form.rationale,
      };
      const r = await api.post("/trade-health/score", payload);
      setResult(r.data);
      // Refresh quota after a successful score
      if (r.data.quota) setQuota((q) => ({ ...(q || {}), ...r.data.quota, remaining: r.data.quota.remaining_after }));
    } catch (err) {
      const status = err.response?.status;
      if (status === 402) {
        // Free-tier limit reached — surface upgrade CTA instead of toast noise
        setQuota((q) => ({ ...(q || {}), remaining: 0 }));
      }
      toast.error(err.response?.data?.detail || t("tradeHealthUi.scoringFailed"));
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;

  const isFree = user.tier === "free";
  const outOfUses = isFree && quota && quota.remaining === 0;

  return (
    <div data-testid="trade-health-page" className="max-w-3xl mx-auto space-y-6">
      <div>
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-amber-600 font-semibold mb-2">
          <Zap size={12} /> {t("tradeHealthUi.tradeHealthScore")}
        </div>
        <h1 className="font-display font-black text-4xl text-slate-900 tracking-tight">
          {t("tradeHealthUi.heading")}
        </h1>
        <p className="mt-3 text-slate-600 max-w-2xl">
          {t("tradeHealthUi.description")}
        </p>
      </div>

      <DiscordFomoLock variant="setups" user={user} />

      {isFree && quota && !outOfUses && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-sm text-amber-900">
          <span className="font-semibold">{t("tradeHealthUi.freePreview")}</span>{" "}
          {t("tradeHealthUi.remainingChecks", { remaining: quota.remaining, limit: quota.limit })}
        </div>
      )}

      {outOfUses ? (
        <UpgradePrompt />
      ) : (
        <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label={t("tradeHealthUi.instrument")} placeholder={t("tradeHealthUi.instrumentPlaceholder")}>
              <Input value={form.instrument} onChange={(e) => patch("instrument", e.target.value)} data-testid="th-instrument" />
            </Field>
            <Field label={t("tradeHealthUi.direction")}>
              <select
                value={form.direction}
                onChange={(e) => patch("direction", e.target.value)}
                data-testid="th-direction"
                className="w-full h-10 rounded-md border border-slate-300 px-3 bg-white text-sm"
              >
                <option value="long">{t("tradeHealthUi.long")}</option>
                <option value="short">{t("tradeHealthUi.short")}</option>
              </select>
            </Field>
            <Field label={t("tradeHealthUi.entry")}>
              <Input type="number" step="any" value={form.entry} onChange={(e) => patch("entry", e.target.value)} data-testid="th-entry" />
            </Field>
            <Field label={t("tradeHealthUi.stop")}>
              <Input type="number" step="any" value={form.stop} onChange={(e) => patch("stop", e.target.value)} data-testid="th-stop" />
            </Field>
            <Field label={t("tradeHealthUi.target")}>
              <Input type="number" step="any" value={form.target} onChange={(e) => patch("target", e.target.value)} data-testid="th-target" />
            </Field>
            <Field label={t("tradeHealthUi.positionSize")}>
              <Input type="number" step="any" value={form.size} onChange={(e) => patch("size", e.target.value)} data-testid="th-size" />
            </Field>
            <Field label={t("tradeHealthUi.accountSize")} className="sm:col-span-3">
              <Input type="number" step="any" value={form.account_size} onChange={(e) => patch("account_size", e.target.value)} data-testid="th-account" />
            </Field>
          </div>

          <Field label={t("tradeHealthUi.rationaleLabel")}>
            <Textarea
              value={form.rationale}
              onChange={(e) => patch("rationale", e.target.value)}
              rows={3}
              placeholder={t("tradeHealthUi.rationalePlaceholder")}
              data-testid="th-rationale"
            />
          </Field>

          <Button
            type="submit"
            disabled={busy}
            data-testid="th-submit"
            className="w-full h-12 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
          >
            {busy
              ? <><Loader2 size={16} className="mr-2 animate-spin" /> {t("tradeHealthUi.scoringInProgress")}</>
              : <><Sparkles size={16} className="mr-2" /> {t("tradeHealthUi.gradeThisTrade")}</>}
          </Button>
        </form>
      )}

      {result && <ResultCard result={result} />}
    </div>
  );
}

function Field({ label, children, className }) {
  return (
    <div className={className}>
      <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 block mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function ResultCard({ result }) {
  const { t } = useTranslation();
  const s = result.score || 0;
  const tone =
    s >= 75 ? { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-800", ring: "ring-emerald-400" } :
    s >= 55 ? { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-800", ring: "ring-amber-400" } :
              { bg: "bg-rose-50", border: "border-rose-300", text: "text-rose-800", ring: "ring-rose-400" };

  return (
    <div
      data-testid="th-result"
      className={`rounded-2xl border-2 ${tone.border} ${tone.bg} p-6`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-6">
        <div className={`shrink-0 w-24 h-24 rounded-full bg-white ring-4 ${tone.ring} flex flex-col items-center justify-center`}>
          <div className={`font-display font-black text-3xl ${tone.text}`}>{s}</div>
          <div className={`text-[9px] uppercase tracking-widest font-bold ${tone.text}`}>{t("tradeHealthUi.score")}</div>
        </div>
        <div className="flex-1">
          <div className={`text-[10px] uppercase tracking-[0.28em] font-bold ${tone.text}`}>
            {t("tradeHealthUi.theProfessorSays")}
          </div>
          <div className="mt-1 font-display font-bold text-xl text-slate-900 leading-tight">
            {result.verdict}
          </div>
          {result.computed?.risk_reward_ratio !== undefined && (
            <div className="mt-2 text-sm text-slate-600 font-mono">
              R:R = {result.computed.risk_reward_ratio}
              {result.computed.pct_account_risk != null && (
                <span> · account risk = {result.computed.pct_account_risk}%</span>
              )}
            </div>
          )}
        </div>
      </div>

      {(result.strengths?.length || 0) > 0 && (
        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 mb-1.5">{t("tradeHealthUi.whatsWorking")}</div>
          <ul className="space-y-1.5">
            {result.strengths.map((s2, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-800">
                <CheckCircle2 size={14} className="text-emerald-600 mt-0.5 shrink-0" />
                <span>{s2}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(result.risks?.length || 0) > 0 && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest font-bold text-rose-700 mb-1.5">{t("tradeHealthUi.risks")}</div>
          <ul className="space-y-1.5">
            {result.risks.map((r2, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-800">
                <AlertTriangle size={14} className="text-rose-600 mt-0.5 shrink-0" />
                <span>{r2}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.one_thing_to_fix && (
        <div className="mt-5 rounded-xl bg-slate-950 text-white p-4">
          <div className="text-[10px] uppercase tracking-widest font-bold text-amber-400 mb-1">
            {t("tradeHealthUi.oneThingToFix")}
          </div>
          <div className="text-sm leading-relaxed">{result.one_thing_to_fix}</div>
        </div>
      )}
    </div>
  );
}

function UpgradePrompt() {
  const { t } = useTranslation();
  return (
    <div
      data-testid="th-upgrade-prompt"
      className="rounded-2xl border-2 border-amber-400 bg-gradient-to-br from-amber-50 via-white to-blue-50 p-8 text-center"
    >
      <Lock size={32} className="mx-auto text-amber-600 mb-3" />
      <h2 className="font-display font-black text-2xl text-slate-900">
        {t("tradeHealthUi.usedBothFreeChecks")}
      </h2>
      <p className="mt-2 text-slate-600 max-w-md mx-auto">
        {t("tradeHealthUi.upgradePrompt")}
      </p>
      <Link to="/pricing">
        <Button className="mt-5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold h-12 px-8">
          {t("tradeHealthUi.seePlans")} <ArrowRight size={16} className="ml-2" />
        </Button>
      </Link>
    </div>
  );
}
